import { useState, useEffect } from 'react';
import toastr from "toastr";
import axios from "axios";
import { CustomLoading } from 'components/CustomLoading';
import { User, Mail, Shield, Lock, KeyRound, GraduationCap, Building2 } from "lucide-react";

const ViewUser = ({ data }) => {
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(data.isActive);
    const [isLoginLocked, setIsLoginLocked] = useState(data.isLoginLocked);
    const [failedAttempts, setFailedAttempts] = useState(data.failedConsecutivePasswordAttempts);
    const [isTrained, setIsTrained] = useState(data.isTrained || false);

    useEffect(() => {
        setLoading(true);
        fetchUserData();
    }, [data.userId, isActive, isLoginLocked]);

    const fetchUserData = async () => {
        try {
          const response = await axios.get(`/api/support/getuserdata?userId=${data.userId}`);
          if (response.data.success) {
            setIsActive(response.data.isActive);
            setIsLoginLocked(response.data.isLoginLocked);
            setFailedAttempts(response.data.failedAttempts);
            setIsTrained(response.data.isTrained);
          }
          setLoading(false);
        } catch (error) {
          toastr.error("Failed to fetch employee notes", "Error");
        }
      }

      const handleActiveToggle = () => {
        const body = {
            userId: data.userId,
            IsActive: !isActive,
        };
        axios.post("/api/support/updateuserstatus", body)
            .then(response => {
                if (response.data.success) {
                    setIsActive(!isActive);
                    toastr.success("User status updated", "Success");
                } else {
                    toastr.error("Failed to update user status", "Error");
                }
            })
            .catch(error => {
                toastr.error("Failed to update user status", "Error");
            });
    };

    const handleLoginLockedToggle = () => {
        const body = {
            userId: data.userId,
            isLoginLocked: !isLoginLocked,
        };
        axios.post("/api/support/updateuserloginlock", body)
            .then(response => {
                if (response.data.success) {
                    setIsLoginLocked(!isLoginLocked);
                    toastr.success("Login lock status updated", "Success");
                } else {
                    toastr.error("Failed to update login lock status", "Error");
                }
            })
            .catch(error => {
                toastr.error("Failed to update login lock status", "Error");
            });
    };

    const handleFailedAttemptsChange = (event) => {
        setFailedAttempts(event.target.value);
    };

    const handleSaveFailedAttempts = async () => {
        const body = {
            userId: data.userId,
            failedAttempts: failedAttempts,
        };
        axios.post("/api/support/updateFailedAttempts", body)
            .then(response => {
                if(response.data.success)
                    {
                        toastr.success('Failed attempts updated successfully!');
                    } else {
                        toastr.error("Failed save failed attempts", "Error");
                }
            })
            .catch(error => {
                toastr.error("Failed save failed attempts", "Error");
            });
    };

    const handleTrainedChange = () => {
        const body = {
            userId: data.userId,
            isTrained: !isTrained,
        };
        axios.post("/api/support/updatetrainedstatus", body)
            .then(response => {
                if (response.data.success) {
                    setIsTrained(!isTrained);
                    toastr.success("Training status updated", "Success");
                } else {
                    toastr.error("Failed to update training status", "Error");
                }
            })
            .catch(error => {
                toastr.error("Failed to update training status", "Error");
            });
    }

    if (loading) {
        return <CustomLoading />;
    }

    const renderPlanAdminsCompanyList = () => {
		return data.policyHolderNames && data.policyHolderNames.length > 0 ? (
			<div>
				<p style={{ color: "#f44336", fontWeight: "bold", fontSize: "14px", marginBottom: "5px" }}>
					This user is a Plan Admin of the Companies below:
				</p>
				<ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
					{data.policyHolderNames.map((item, i) => (
						<li key={i} style={{ marginBottom: "2px" }}>
							<a
								href="#"
								style={{ textDecoration: "none", color: "#007bff" }}
								onClick={(e) => {
									e.preventDefault(); // Prevent default anchor behavior
									window.open(
										"/ops/enroll.policy.record?policy_id=" + item.id,
										"_blank"
									);
								}}
							>
								{item.name}
							</a>
							{item.readOnly && <span style={{ marginLeft: "6px", color: "#856404", backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "3px", padding: "1px 6px", fontSize: "12px", fontWeight: 500 }}>Read Only</span>}
							{!item.isActive && <span style={{ marginLeft: "6px", color: "#721c24", backgroundColor: "#f8d7da", border: "1px solid #f5c6cb", borderRadius: "3px", padding: "1px 6px", fontSize: "12px", fontWeight: 500 }}>Inactive</span>}
						</li>
					))}
				</ul>
			</div>
		) : null;
	};

    return (
        <div className="mt-3 view-detail-card">
            <div className="view-detail-row">
                <div className="view-detail-label"><User size={15}/> Username</div>
                <div className="view-detail-value"><b>{data.username}</b></div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><User size={15}/> Name</div>
                <div className="view-detail-value"><b>{data.lastName}, {data.firstName}</b></div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Mail size={15}/> Email</div>
                <div className="view-detail-value">
                    <a
                        href={"mailto:" + data.email}
                        target="_blank"
                        style={{color: "blue"}}
                    >
                        {data.email}
                    </a>
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Is Active</div>
                <div className="view-detail-value">
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="active-checkbox"
                            checked={isActive}
                            onChange={handleActiveToggle}
                        />
                        <label className="form-check-label" htmlFor="active-checkbox">{isActive ? "Yes" : "No"}</label>
                    </div>
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Lock size={15}/> Is Login Locked</div>
                <div className="view-detail-value">
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="is-login-locked-checkbox"
                            checked={isLoginLocked}
                            onChange={handleLoginLockedToggle}
                        />
                        <label className="form-check-label" htmlFor="is-login-locked-checkbox">{isLoginLocked ? "Yes" : "No"}</label>
                    </div>
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><KeyRound size={15}/> Failed Consecutive Password Attempts</div>
                <div className="view-detail-value" style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "10px"}}>
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        value={failedAttempts}
                        onChange={handleFailedAttemptsChange}
                        min="0"
                        style={{width: "80px"}}
                    />
                    <button
                        onClick={handleSaveFailedAttempts}
                        className="btn btn-primary btn-sm"
                    >
                        Save
                    </button>
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Is Two Factor Enabled</div>
                <div className="view-detail-value">{data.isTwoFactorEnabled ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Is Onboarding Completed</div>
                <div className="view-detail-value">{data.isOnboardingCompleted ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><GraduationCap size={15}/> Is Trained</div>
                <div className="view-detail-value">
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="is-trained-checkbox"
                            checked={isTrained}
                            onChange={handleTrainedChange}
                        />
                        <label className="form-check-label" htmlFor="is-trained-checkbox">{isTrained ? "Yes" : "No"}</label>
                    </div>
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Building2 size={15}/> Assigned Companies</div>
                <div className="view-detail-value">{renderPlanAdminsCompanyList()}</div>
            </div>
        </div>
    );
};

export default ViewUser;
